/**
 * Axios HTTP client configured for the Velosynt API.
 *
 * - Attaches the JWT access token to every request via an interceptor.
 * - Automatically attempts a token refresh on 401 responses.
 * - Queues concurrent requests during refresh to avoid race conditions.
 */

import axios, { type AxiosError, type InternalAxiosRequestConfig } from 'axios';
import { queryClient } from '@/store/queryClient';
import {useAuthStore} from '@/store/authStore';

const API_BASE_URL = import.meta.env.VITE_API_URL ?? '/api';

const apiClient = axios.create({
  baseURL: API_BASE_URL,
  headers: { 'Content-Type': 'application/json' },
});

/* ------------------------------------------------------------------ */
/* Request interceptor: attach bearer token                            */
/* ------------------------------------------------------------------ */

apiClient.interceptors.request.use((config: InternalAxiosRequestConfig) => {
  const token = localStorage.getItem('access_token');
   if (token && config.headers && !config.headers.Authorization) {
    config.headers.Authorization = `Bearer ${token}`;
  }
  return config;
});

/* ------------------------------------------------------------------ */
/* Response interceptor: handle 401 with token refresh                 */
/* ------------------------------------------------------------------ */

let isRefreshing = false;
let pendingRequests: Array<{resolve:(token:string)=>void;reject:(error:unknown)=>void}> = [];

/** Replay queued requests once a new token is available. */
function onRefreshed(token: string): void {
  pendingRequests.forEach((pending) => pending.resolve(token));
  pendingRequests = [];
}
function isSessionExpired(error:unknown):boolean{
  const response=(error as {response?:{status?:number;data?:{error?:string;detail?:{error?:string}}}})?.response;
  return response?.status===401&&(response.data?.error==='session_expired'||response.data?.detail?.error==='session_expired');
}
function expireLocalSession(error:unknown){
  pendingRequests.forEach(pending=>pending.reject(error));pendingRequests=[];
  localStorage.removeItem('access_token');localStorage.removeItem('refresh_token');sessionStorage.removeItem('enrollment_token');
  queryClient.clear();useAuthStore.setState({user:null,isAuthenticated:false,isLoading:false,mfaChallenge:null});
  window.location.assign('/login?reason=session_expired');
}

apiClient.interceptors.response.use(
  (response) => response,
  async (error: AxiosError) => {
    // A policy-expired session must never enter token refresh or replay queues.
    if(isSessionExpired(error)){expireLocalSession(error);return Promise.reject(error);}
    const originalRequest = error.config as InternalAxiosRequestConfig & { _retry?: boolean };

    // Only attempt refresh on 401 and if we haven't already retried.
    if (!originalRequest || error.response?.status !== 401 || originalRequest._retry
      || ['/v1/auth/login','/v1/auth/logout','/v1/bootstrap','/v1/auth/enrollment','/v1/auth/activation','/v1/auth/oauth'].some(path=>originalRequest.url?.startsWith(path))) {
      return Promise.reject(error);
    }

    const refreshToken = localStorage.getItem('refresh_token');
    if (!refreshToken) {
      // No refresh token — force logout.
      localStorage.removeItem('access_token');
      localStorage.removeItem('refresh_token');
      window.location.href = '/login';
      return Promise.reject(error);
    }

    if (isRefreshing) {
      // Another refresh is in progress — queue this request.
      return new Promise((resolve,reject) => {
        pendingRequests.push({reject,resolve:(token: string) => {
          if (originalRequest.headers) {
            originalRequest.headers.Authorization = `Bearer ${token}`;
          }
          originalRequest._retry = true;
          resolve(apiClient(originalRequest));
        }});
      });
    }

    isRefreshing = true;
    originalRequest._retry = true;

    try {
      const { data } = await axios.post<{ access_token: string; refresh_token: string }>(
        `${API_BASE_URL}/v1/auth/refresh`,
        null,
        { headers: { Authorization: `Bearer ${refreshToken}` } },
      );

      if (localStorage.getItem('refresh_token') !== refreshToken) throw new Error('Session changed during refresh');

      localStorage.setItem('access_token', data.access_token);
      localStorage.setItem('refresh_token', data.refresh_token);

      if (originalRequest.headers) {
        originalRequest.headers.Authorization = `Bearer ${data.access_token}`;
      }

      onRefreshed(data.access_token);
      return apiClient(originalRequest);
    } catch (refreshError) {
      if(isSessionExpired(refreshError)){expireLocalSession(refreshError);return Promise.reject(refreshError);}
      pendingRequests.forEach(pending=>pending.reject(error));
      pendingRequests=[];
      if (localStorage.getItem('refresh_token') !== refreshToken) return Promise.reject(error);
      // Refresh failed — force logout.
      localStorage.removeItem('access_token');
      localStorage.removeItem('refresh_token');
      queryClient.clear();
      window.location.href = '/login';
      return Promise.reject(error);
    } finally {
      isRefreshing = false;
    }
  },
);

export default apiClient;
